import { Add, ExpandMore, Microphone } from '@tunasong/icons'
import {
  Button,
  ButtonGroup,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Typography,
  useUploadFiles,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef } from 'react'
import styles from './home-header.module.css'

export interface HomeHeaderProps {
  /** Desktop shows Record idea here; on phone the floating record button takes its place */
  showRecord: boolean
  /** A create is in flight */
  busy: boolean
  /** File types the Upload file picker accepts, as an `accept` attribute value */
  accept: string
  onNewSong(): void
  onNewSetlist(): void
  onNewPage(): void
  onNewSpace(): void
  onRecord(): void
}

/**
 * Home page header: the page's only h1, Record idea (secondary) and the New song split button (primary).
 * The primary action stays "New song" in every state, first run included; the chevron holds the rest.
 */
export const HomeHeader: FC<HomeHeaderProps> = props => {
  const { showRecord, busy, accept, onNewSong, onNewSetlist, onNewPage, onNewSpace, onRecord } = props

  // Uploads land at the root, which Home lists as Unfiled
  const { handleChange } = useUploadFiles()
  const uploadRef = useRef<HTMLInputElement | null>(null)
  const handleUploadFile = () => uploadRef.current?.click()

  return (
    <div className={styles.header}>
      <Typography variant="h1" className={styles.title}>
        Home
      </Typography>
      <div className={styles.actions}>
        {showRecord ? (
          <Button variant="outlined" className={styles.button} startIcon={<Microphone />} onClick={onRecord}>
            Record idea
          </Button>
        ) : null}
        <ButtonGroup aria-label="Create" className={styles.split}>
          <Button
            variant="contained"
            className={`${styles.button} ${styles.splitMain}`}
            startIcon={<Add />}
            disabled={busy}
            onClick={onNewSong}
          >
            New song
          </Button>
          <Menu>
            <MenuTrigger
              render={
                <Button
                  variant="contained"
                  className={`${styles.button} ${styles.splitChevron}`}
                  aria-label="Create something else"
                  disabled={busy}
                >
                  <ExpandMore />
                </Button>
              }
            />
            <MenuContent>
              <MenuItem onClick={onNewSetlist}>New setlist</MenuItem>
              <MenuItem onClick={onNewPage}>New page</MenuItem>
              <MenuItem onClick={onNewSpace}>New space</MenuItem>
              <MenuItem onClick={handleUploadFile}>Upload file</MenuItem>
            </MenuContent>
          </Menu>
        </ButtonGroup>
      </div>
      <input ref={uploadRef} type="file" multiple accept={accept} hidden onChange={handleChange(null)} />
    </div>
  )
}

export default HomeHeader
